import { useState } from 'react'
import { useTranslation } from '@/contexts/TranslationContext'
import { Alert } from '@/components/ui'

interface BillingWorkspaceStepProps {
  initialData: {
    name: string
    pix_key: string
  }
  userName: string
  onNext: (data: any) => void
  onBack: () => void
  isLoading: boolean
}

export function BillingWorkspaceStep({
  initialData,
  userName,
  onNext,
  onBack,
  isLoading
}: BillingWorkspaceStepProps) {
  const { t } = useTranslation()
  const [name, setName] = useState(initialData.name)
  const [pixKey, setPixKey] = useState(initialData.pix_key)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validate = () => {
    const newErrors: Record<string, string> = {}

    if (!name.trim()) {
      newErrors.name = t('onboarding.billing.workspace.errors.nameRequired')
    } else if (name.trim().length < 3) {
      newErrors.name = t('onboarding.billing.workspace.errors.nameMinLength')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    if (!validate()) return

    onNext({
      name: name.trim(),
      pix_key: pixKey.trim()
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
          {t('onboarding.billing.workspace.title')}
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          {t('onboarding.billing.workspace.subtitle', { name: userName || 'você' })}
        </p>
      </div>

      {/* Form */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg p-8 space-y-6">
        {/* Workspace Name */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {t('onboarding.billing.workspace.nameLabel')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('onboarding.billing.workspace.namePlaceholder')}
            disabled={isLoading}
            className={`w-full px-4 py-2 rounded-lg border ${
              errors.name ? 'border-red-300 dark:border-red-600' : 'border-slate-300 dark:border-slate-600'
            } bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 ${
              errors.name ? 'focus:ring-red-500' : 'focus:ring-emerald-500'
            } disabled:opacity-50 transition-colors`}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>
          )}
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {t('onboarding.billing.workspace.nameHelp')}
          </p>
        </div>

        {/* PIX Key */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {t('pix.label')}
          </label>
          <input
            type="text"
            value={pixKey}
            onChange={(e) => setPixKey(e.target.value)}
            placeholder={t('pix.placeholder')}
            disabled={isLoading}
            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 transition-colors"
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {t('pix.help')}
          </p>
        </div>
      </div>

      {/* Note */}
      <Alert variant="info">
        <p className="text-sm">{t('onboarding.billing.workspace.note')}</p>
      </Alert>

      {/* Buttons */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          disabled={isLoading}
          className="px-8 py-3 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 disabled:opacity-50 text-slate-900 dark:text-white rounded-lg font-medium transition-colors"
        >
          {t('onboarding.billing.backButton')}
        </button>
        <button
          onClick={handleNext}
          disabled={isLoading}
          className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-400 dark:disabled:bg-slate-700 text-white rounded-lg font-medium transition-colors"
        >
          {isLoading ? t('onboarding.billing.loading') : t('onboarding.billing.nextButton')}
        </button>
      </div>
    </div>
  )
}
